import React from 'react';
import { Link } from 'react-router-dom';
import '../../global.css';
import Navbar from '../../components/Navbar';
import SiteFooter from '../../components/SiteFooter';
import NeuralCanvas from '../../components/NeuralCanvas';
import { useSEO } from '../../hooks/useSEO';

function Section({ num, title, children }) {
  return (
    <section style={{ marginBottom: 52 }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 10 }}>{num}</div>
      <h2 style={{ fontSize: 22, fontWeight: 500, letterSpacing: '-0.02em', marginBottom: 16, color: 'var(--fg)' }}>{title}</h2>
      <div style={{ fontSize: 14, lineHeight: 1.82, color: 'var(--fg-mid)' }}>{children}</div>
    </section>
  );
}

function P({ children }) { return <p style={{ marginBottom: 14 }}>{children}</p>; }
function Ul({ items }) {
  return (
    <ul style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '12px 0' }}>
      {items.map(item => (
        <li key={item} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'var(--fg-dim)', marginTop: 8, flexShrink: 0 }} />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function Terms() {
  useSEO({
    title: 'Terms of Service',
    description: 'Cybernate\'s terms of service governing use of the platform, API, and developer tools.',
    path: '/terms',
  });
  return (
    <>
      <NeuralCanvas />
      <div className="neural-page">
      <Navbar />

      <div style={{ borderBottom: '1px solid var(--line)', padding: '64px 0 48px' }}>
        <div style={{ maxWidth: 'var(--maxw)', margin: '0 auto', padding: '0 var(--pad-x)' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--accent)', letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: 16 }}>Legal</div>
          <h1 style={{ fontSize: 'clamp(28px, 4vw, 44px)', fontWeight: 400, letterSpacing: '-0.03em', marginBottom: 12 }}>Terms of Service</h1>
          <p style={{ fontSize: 14, color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)', letterSpacing: '0.06em' }}>
            Last updated: May 1, 2026 · Effective: May 1, 2026
          </p>
        </div>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '56px var(--pad-x) 96px' }}>

        <Section num="01 :: Agreement" title="Agreement to Terms">
          <P>By accessing Cybernate's API platform, developer portal, or any associated services, you agree to be bound by these Terms of Service. If you do not agree, do not use the platform.</P>
          <P>These terms apply to all users — individual developers, businesses, and organizations operating under a registered Cybernate account.</P>
        </Section>

        <Section num="02 :: Services" title="The Services">
          <P>Cybernate provides access to AI tools (language, vision, voice, and more), developer tooling, and infrastructure across Africa. Services are prepaid — you add credits to your account which are spent as you use the platform.</P>
        </Section>

        <Section num="03 :: Acceptable Use" title="Acceptable Use">
          <P>You may use the Cybernate platform only for lawful purposes. The following uses are expressly prohibited:</P>
          <Ul items={[
            'Generating, distributing, or enabling content that incites violence or hatred',
            'Creating surveillance systems targeting individuals without consent',
            'Impersonating individuals or organizations using AI-generated content',
            'Sharing your account access credentials with third parties',
            'Using Cybernate outputs to build a competing product without prior written agreement',
            'Using the platform to violate any applicable African or international law',
          ]} />
        </Section>

        <Section num="04 :: Billing" title="Billing & Credits">
          <P>Access to Cybernate APIs requires a prepaid credit balance. Credits are non-refundable once consumed. Unused credits expire 12 months after purchase.</P>
          <P>New accounts receive $50 in promotional credits that expire 90 days from account creation. These credits are non-transferable and may not be redeemed for cash.</P>
          <P>Cybernate reserves the right to modify pricing with 30 days' notice. Existing credit balances are not affected by price changes.</P>
        </Section>

        <Section num="05 :: Data & Privacy" title="Data & Privacy">
          <P>Your use of the platform is also governed by our <Link to="/legal" style={{ color: 'var(--accent)' }}>Privacy Policy</Link>. By using the platform you consent to the data practices described therein.</P>
          <P>You retain ownership of all data you submit to the API. Cybernate claims no intellectual property rights over your inputs or outputs.</P>
        </Section>

        <Section num="06 :: SLA & Uptime" title="Service Level Agreement">
          <P>Cybernate targets 99.9% API availability measured monthly. Downtime is defined as complete API unavailability for more than 1 consecutive minute, excluding scheduled maintenance (announced 48 hours in advance).</P>
          <P>In the event of a monthly SLA breach, affected accounts receive credit equal to 10× the value of downtime minutes, applied automatically to the next billing cycle.</P>
        </Section>

        <Section num="07 :: Termination" title="Termination">
          <P>You may terminate your account at any time by emailing <a href="mailto:info@cybernate.ai" style={{ color: 'var(--accent)' }}>info@cybernate.ai</a>. Account termination will be processed within 5 business days. Remaining credit balances are forfeited upon termination.</P>
          <P>Cybernate reserves the right to suspend or terminate accounts that violate these terms, with immediate effect in cases of security threats or prohibited use.</P>
        </Section>

        <Section num="08 :: Liability" title="Limitation of Liability">
          <P>The Cybernate platform is provided "as is". We do not warrant that the service will be uninterrupted, error-free, or suitable for any particular purpose. Our total liability to you shall not exceed the amount you paid for the service in the three months preceding the claim.</P>
        </Section>

        <Section num="09 :: Governing Law" title="Governing Law">
          <P>These terms are governed by the laws of the Federal Republic of Nigeria. Any dispute arising from these terms shall be subject to the exclusive jurisdiction of the courts of Lagos State, Nigeria.</P>
        </Section>

        <div style={{ padding: '24px', background: 'var(--bg-1)', border: '1px solid var(--line)', borderRadius: 'var(--r-md)', marginTop: 8 }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--fg-dim)', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 10 }}>Questions?</div>
          <p style={{ fontSize: 13, color: 'var(--fg-mid)', marginBottom: 12 }}>For legal enquiries, contact our team:</p>
          <a href="mailto:legal@cybernate.ai" style={{ color: 'var(--accent)', fontSize: 13 }}>legal@cybernate.ai</a>
        </div>

      </div>

      <SiteFooter />
      </div>
    </>
  );
}
